// Custom hooks for alert-related API calls
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  getRegions,
  createDefaultAlert,
  createCustomAlert,
  getCommoditySkuPrice,
  getCommoditySkuVolume,
  getDefaultAlerts,
  getCustomAlerts,
  updateDefaultAlert,
  updateCustomAlert,
  deleteDefaultAlert,
  deleteCustomAlert,
  getDefaultAlertsNotifications,
  getCustomAlertsNotifications,
} from '../api';
import { useUser } from '../../context/UserContext';

const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000;
const ONE_HOUR_IN_MS = 60 * 60 * 1000;
const EMPTY_REGIONS = Object.freeze({});

/**
 * Custom hook to fetch regions based on alert type and commodity
 * @param {string} alertType - The alert type (price, volume, rain, temperature, weather, market)
 * @param {string} commodity - The selected commodity
 * @param {string} defaultOrCustom - 'default' for smart alerts, 'custom' for custom alerts
 * @returns {Object} - { regions, isLoading, isError, error, refetch }
 */
export const useRegions = (alertType, commodity, defaultOrCustom = 'default') => {
  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['regions', alertType, commodity, defaultOrCustom],
    queryFn: () => getRegions(alertType, commodity, defaultOrCustom),
    enabled: !!alertType && !!commodity && alertType !== 'market', // Market alerts don't use regions
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
    gcTime: 24 * 60 * 60 * 1000, // 24 hours
  });

  // Regions data comes as { countryName: [cities] } format.
  // Use a stable empty object so consumers don't re-render forever on `data || {}`.
  const regions = data ?? EMPTY_REGIONS;

  return {
    regions,
    isLoading: isLoading || isFetching,
    isError,
    error,
    refetch,
  };
};

/**
 * Custom hook to create a smart (default) alert
 * @returns {Object} - mutation object with mutate, mutateAsync, isLoading, isError, error, etc.
 */
export const useCreateSmartAlert = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (alertData) => createDefaultAlert(alertData),
    onSuccess: () => {
      // Invalidate default alerts query to refetch the list
      queryClient.invalidateQueries({ queryKey: ['defaultAlerts'] });
    },
  });
};

/**
 * Custom hook to create a custom alert
 * @returns {Object} - mutation object with mutate, mutateAsync, isLoading, isError, error, etc.
 */
export const useCreateCustomAlert = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (alertData) => createCustomAlert(alertData),
    onSuccess: () => {
      // Invalidate custom alerts query to refetch the list
      queryClient.invalidateQueries({ queryKey: ['customAlerts'] });
    },
  });
};

/**
 * Custom hook to fetch commodity SKU price data
 * Used for Price alerts to populate variety, size, grade, type, package dropdowns
 * @param {string} commodity - The selected commodity
 * @returns {Object} - { data, isLoading, isError, error }
 */
export const useGetCommoditySkuPrice = (commodity) => {
  return useQuery({
    queryKey: ['getCommoditySkuPrice', commodity],
    queryFn: () => getCommoditySkuPrice(commodity),
    enabled: !!commodity,
    staleTime: ONE_DAY_IN_MS,
    gcTime: ONE_DAY_IN_MS,
  });
};

/**
 * Custom hook to fetch commodity SKU volume data
 * Used for Volume alerts to populate variety, type dropdowns
 * @param {string} commodity - The selected commodity
 * @returns {Object} - { data, isLoading, isError, error }
 */
export const useGetCommoditySkuVolume = (commodity) => {
  return useQuery({
    queryKey: ['getCommoditySkuVolume', commodity],
    queryFn: () => getCommoditySkuVolume(commodity),
    enabled: !!commodity,
    staleTime: ONE_DAY_IN_MS,
    gcTime: ONE_DAY_IN_MS,
  });
};

// ============================================
// Alerts List Hooks - Fetch, Update, Delete
// ============================================

/**
 * Custom hook to fetch default (smart) alerts
 * @returns {Object} - { data, isLoading, isFetching, isError, error, refetch }
 */
export const useGetDefaultAlerts = () => {
  const { email } = useUser();

  return useQuery({
    queryKey: ['defaultAlerts', email],
    queryFn: () => getDefaultAlerts(email),
    enabled: !!email,
    staleTime: ONE_HOUR_IN_MS,
    gcTime: ONE_DAY_IN_MS,
  });
};

/**
 * Custom hook to fetch custom alerts
 * @returns {Object} - { data, isLoading, isFetching, isError, error, refetch }
 */
export const useGetCustomAlerts = () => {
  const { email } = useUser();

  return useQuery({
    queryKey: ['customAlerts', email],
    queryFn: () => getCustomAlerts(email),
    enabled: !!email,
    staleTime: ONE_HOUR_IN_MS,
    gcTime: ONE_DAY_IN_MS,
  });
};

/**
 * Custom hook to update (toggle) a default (smart) alert
 * Optimistically updates the cache for better UX
 * @returns {Object} - mutation object with mutate, mutateAsync, isPending, isError, error, etc.
 */
export const useUpdateDefaultAlert = () => {
  const { email } = useUser();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (alert) => updateDefaultAlert(email, alert),
    onMutate: async (alert) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['defaultAlerts', email] });

      // Snapshot the previous value
      const previousAlerts = queryClient.getQueryData(['defaultAlerts', email]);

      // Optimistically update the cache
      queryClient.setQueryData(['defaultAlerts', email], (oldData) => {
        if (!oldData?.alerts) return oldData;
        return {
          ...oldData,
          alerts: oldData.alerts.map((a) =>
            a.alert_id === alert.alert_id ? { ...a, enabled: !a.enabled } : a
          ),
        };
      });

      return { previousAlerts };
    },
    onError: (err, alert, context) => {
      // Rollback on error
      queryClient.setQueryData(['defaultAlerts', email], context.previousAlerts);
    },
    onSettled: () => {
      // Refetch after mutation to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['defaultAlerts', email] });
    },
  });
};

/**
 * Custom hook to update (toggle) a custom alert
 * Optimistically updates the cache for better UX
 * @returns {Object} - mutation object with mutate, mutateAsync, isPending, isError, error, etc.
 */
export const useUpdateCustomAlert = () => {
  const { email } = useUser();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (alert) => updateCustomAlert(email, alert),
    onMutate: async (alert) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['customAlerts', email] });

      // Snapshot the previous value
      const previousAlerts = queryClient.getQueryData(['customAlerts', email]);

      // Optimistically update the cache
      queryClient.setQueryData(['customAlerts', email], (oldData) => {
        if (!oldData?.alerts) return oldData;
        return {
          ...oldData,
          alerts: oldData.alerts.map((a) =>
            a.alert_id === alert.alert_id ? { ...a, enabled: !a.enabled } : a
          ),
        };
      });

      return { previousAlerts };
    },
    onError: (err, alert, context) => {
      // Rollback on error
      queryClient.setQueryData(['customAlerts', email], context.previousAlerts);
    },
    onSettled: () => {
      // Refetch after mutation to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['customAlerts', email] });
    },
  });
};

/**
 * Custom hook to delete a default (smart) alert
 * Optimistically updates the cache for better UX
 * @returns {Object} - mutation object with mutate, mutateAsync, isPending, isError, error, etc.
 */
export const useDeleteDefaultAlert = () => {
  const { email } = useUser();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (alert) => deleteDefaultAlert(email, alert),
    onMutate: async (alert) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['defaultAlerts', email] });

      // Snapshot the previous value
      const previousAlerts = queryClient.getQueryData(['defaultAlerts', email]);

      // Optimistically remove from cache
      queryClient.setQueryData(['defaultAlerts', email], (oldData) => {
        if (!oldData?.alerts) return oldData;
        return {
          ...oldData,
          alerts: oldData.alerts.filter((a) => a.alert_id !== alert.alert_id),
        };
      });

      return { previousAlerts };
    },
    onError: (err, alert, context) => {
      // Rollback on error
      queryClient.setQueryData(['defaultAlerts', email], context.previousAlerts);
    },
    onSettled: () => {
      // Refetch after mutation to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['defaultAlerts', email] });
    },
  });
};

/**
 * Custom hook to delete a custom alert
 * Optimistically updates the cache for better UX
 * @returns {Object} - mutation object with mutate, mutateAsync, isPending, isError, error, etc.
 */
export const useDeleteCustomAlert = () => {
  const { email } = useUser();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (alert) => deleteCustomAlert(email, alert),
    onMutate: async (alert) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['customAlerts', email] });

      // Snapshot the previous value
      const previousAlerts = queryClient.getQueryData(['customAlerts', email]);

      // Optimistically remove from cache
      queryClient.setQueryData(['customAlerts', email], (oldData) => {
        if (!oldData?.alerts) return oldData;
        return {
          ...oldData,
          alerts: oldData.alerts.filter((a) => a.alert_id !== alert.alert_id),
        };
      });

      return { previousAlerts };
    },
    onError: (err, alert, context) => {
      // Rollback on error
      queryClient.setQueryData(['customAlerts', email], context.previousAlerts);
    },
    onSettled: () => {
      // Refetch after mutation to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['customAlerts', email] });
    },
  });
};

// ============================================
// Alert Notifications Hooks
// ============================================

/**
 * Get the current ISO week number (accurate calculation)
 * Uses the ISO 8601 standard where week 1 contains the first Thursday of the year
 * @param {Date} date - The date to get the week number for (defaults to now)
 * @returns {number} - The current week number (1-53)
 */
export const getCurrentWeekNumber = (date = new Date()) => {
  const target = new Date(date.valueOf());
  const dayNumber = (date.getDay() + 6) % 7; // Make Monday = 0
  target.setDate(target.getDate() - dayNumber + 3); // Thursday
  const firstThursday = new Date(target.getFullYear(), 0, 4);
  const firstThursdayDayNumber = (firstThursday.getDay() + 6) % 7;
  firstThursday.setDate(firstThursday.getDate() - firstThursdayDayNumber + 3);

  return (
    1 +
    Math.round(
      (target.getTime() - firstThursday.getTime()) / (7 * 24 * 3600 * 1000)
    )
  );
};

/**
 * Get the start date of a given ISO week number
 * @param {number} weekNumber - The week number
 * @param {number} year - The year (defaults to current year)
 * @returns {Date} - The start date (Monday) of that week
 */
export const getWeekStartDate = (weekNumber, year = new Date().getFullYear()) => {
  const simple = new Date(year, 0, 4); // Jan 4 is always in week 1
  const dayOfWeek = simple.getDay() || 7; // Make Sunday = 7
  const weekStart = new Date(simple);

  // Move to Monday of week 1
  weekStart.setDate(simple.getDate() - dayOfWeek + 1 + (weekNumber - 1) * 7);

  return weekStart;
};

/**
 * Get the end date of a given ISO week number (6 days after start)
 * @param {number} weekNumber - The week number
 * @param {number} year - The year (defaults to current year)
 * @returns {Date} - The end date (Sunday) of that week
 */
export const getWeekEndDate = (weekNumber, year = new Date().getFullYear()) => {
  const weekStart = getWeekStartDate(weekNumber, year);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  return weekEnd;
};

/**
 * Get initial selected weeks (current week and 3 previous weeks)
 * @returns {number[]} - Array of week numbers
 */
export const getInitialSelectedWeeks = () => {
  const currentWeek = getCurrentWeekNumber();
  return Array.from({ length: 4 }, (_, i) => currentWeek - i)
    .filter((week) => week >= 1)
    .sort((a, b) => a - b);
};

/**
 * Custom hook to fetch default (smart) alert notifications
 * @param {number[]} weeks - Array of week numbers to fetch notifications for
 * @returns {Object} - { data, isLoading, isFetching, isError, error, refetch }
 */
export const useGetDefaultAlertsNotifications = (weeks) => {
  const { email } = useUser();
  const { i18n } = useTranslation();
  const language = i18n.language || 'en';

  return useQuery({
    queryKey: ['defaultAlertsNotifications', email, weeks.join(','), language],
    queryFn: () => getDefaultAlertsNotifications(email, weeks, language),
    enabled: !!email && weeks.length > 0,
    staleTime: ONE_HOUR_IN_MS,
    gcTime: ONE_HOUR_IN_MS,
  });
};

/**
 * Custom hook to fetch custom alert notifications
 * @param {number[]} weeks - Array of week numbers to fetch notifications for
 * @returns {Object} - { data, isLoading, isFetching, isError, error, refetch }
 */
export const useGetCustomAlertsNotifications = (weeks) => {
  const { email } = useUser();
  const { i18n } = useTranslation();
  const language = i18n.language || 'en';

  return useQuery({
    queryKey: ['customAlertsNotifications', email, weeks.join(','), language],
    queryFn: () => getCustomAlertsNotifications(email, weeks, language),
    enabled: !!email && weeks.length > 0,
    staleTime: ONE_HOUR_IN_MS,
    gcTime: ONE_HOUR_IN_MS,
  });
};

